import { isApiError } from "@positive/api-client";
import { Button, MessageStrip, type MessageStripDomRef } from "@ui5/webcomponents-react";
import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { fieldInError, markMissing, missingRequiredFields, namedFields } from "./requiredFields";

import "./ApiErrorMessage.css";

/**
 * Translates an error code to a message (ADR-0009/D-2: the API returns codes, the client translates). Unknown
 * codes fall back to a generic message; the trace id is shown so support can find the server log.
 */
// eslint-disable-next-line react-refresh/only-export-components -- small helper shared with dialogs
export function useErrorText(): (error: unknown) => { message: string; traceId: string | undefined } {
  const { t, i18n } = useTranslation("errors");
  return (error) => {
    if (!isApiError(error)) return { message: t("generic"), traceId: undefined };
    const key = `codes.${error.code}`;
    // The server names a field by its API name ("name", "lines.amount"): show its label where one is known.
    const args = { ...(error.args as Record<string, string>) };
    // IN-S-23: an amount or quantity the server sends at its storage precision ("100.000000") reads without the
    // padding zeros ("100").
    for (const [name, value] of Object.entries(args)) {
      if (typeof value === "string" && /^-?\d+\.\d+$/u.test(value)) args[name] = value.replace(/\.?0+$/u, "");
    }
    // SRV-B-01 / SRV-B-02: codes the server sends as values ("chequeBox", "reconciliationDifferenceAccount") read as
    // words; "cashBox|bank" (either) is joined with the list separator.
    for (const name of ["expected", "account"]) {
      const value = args[name];
      if (typeof value !== "string" || value === "") continue;
      args[name] = value
        .split("|")
        .map((part) => {
          const valueKey = `argValues.${part}`;
          return i18n.exists(valueKey, { ns: "errors" }) ? t(valueKey as "codes.unexpected_error") : part;
        })
        .join(t("orSeparator"));
    }
    const field = args["field"];
    const fieldKey = `fieldNames.${String(field).replace(/\./gu, "_")}`;
    if (typeof field === "string" && i18n.exists(fieldKey, { ns: "errors" })) {
      args["field"] = t(fieldKey as "codes.unexpected_error");
    }
    // SRV-H-15: a code refused for several reasons has a text per reason (`codesByReason.<code>.<reason>`).
    const byReason = `codesByReason.${error.code}.${String(error.args["reason"])}`;
    if (typeof error.args["reason"] === "string" && i18n.exists(byReason, { ns: "errors" })) {
      const traceRef = error.status >= 500 || error.status === 0 ? error.traceId : undefined;
      return { message: t(byReason as "codes.unexpected_error", args), traceId: traceRef };
    }
    const known = i18n.exists(key, { ns: "errors" });
    const text = known ? t(key as "codes.unexpected_error", args) : t("generic");
    // A line-level refusal whose text does not name the line (SRV-B-01): say which line.
    const lineTemplate: unknown = i18n.getResource(i18n.language, "errors", key);
    const line = args["line"];
    const message =
      known &&
      (typeof line === "string" || typeof line === "number") &&
      typeof lineTemplate === "string" &&
      !lineTemplate.includes("{line}")
        ? t("atLine", { line, message: text })
        : text;
    // The trace reference is for support when the server failed; on a refused input it only looks like a crash.
    const traceId = error.status >= 500 || error.status === 0 ? error.traceId : undefined;
    // "Line 2 is not valid." alone does not say what to fix: add the reason code the API sends with it.
    const reason = error.args["reason"];
    const reasonKey = `reasons.${String(reason)}`;
    const template: unknown = i18n.getResource(i18n.language, "errors", key);
    const quotesReason = typeof template === "string" && template.includes("{reason}");
    if (typeof reason === "string" && !quotesReason && i18n.exists(reasonKey, { ns: "errors" })) {
      return { message: `${message} ${t(reasonKey as "codes.unexpected_error")}`, traceId };
    }
    return { message, traceId };
  };
}

/** The per-field errors a settings save reports in `args.errors` (`[{ field, code }]`), or an empty list. */
function listedFieldErrors(error: unknown): { field: string; code: string }[] {
  if (!isApiError(error)) return [];
  const listed = error.args["errors"];
  if (!Array.isArray(listed)) return [];
  return listed.flatMap((item: unknown) => {
    if (typeof item !== "object" || item === null) return [];
    const { field, code } = item as { field?: unknown; code?: unknown };
    return typeof field === "string" && field !== "" && typeof code === "string" ? [{ field, code }] : [];
  });
}

export interface ApiErrorMessageProps {
  error: unknown;
  onRetry?: () => void;
}

/** Negative message strip for a failed request, with an optional Retry. */
export function ApiErrorMessage({ error, onRetry }: ApiErrorMessageProps) {
  const { t, i18n } = useTranslation("errors");
  const describe = useErrorText();
  const { message, traceId } = describe(error);
  const strip = useRef<MessageStripDomRef>(null);
  const [missing, setMissing] = useState<string[]>([]);
  const [named, setNamed] = useState<string | null>(null);
  const [invalid, setInvalid] = useState<string[]>([]);
  // "Some fields need your attention" alone does not say which: name (and mark) the required fields left empty in
  // the form this message belongs to - its dialog, else its object page or page.
  useLayoutEffect(() => {
    // A save error at the top of a long form is off screen: bring it into view so the user sees what happened.
    // (Test DOMs do not implement scrolling.)
    const element: HTMLElement | null = strip.current;
    if (element && typeof element.scrollIntoView === "function") {
      element.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
    const form = strip.current?.closest("ui5-dialog, [data-testid$='object-page'], main");
    // Any refused input (400 / 422): an empty required field explains it better than, e.g., the code-format rule
    // the server applies to an empty code.
    if (!form || !isApiError(error) || (error.status !== 400 && error.status !== 422)) {
      setMissing([]);
      setNamed(null);
      setInvalid([]);
      return;
    }
    // A refused save that lists its fields (settings: args.errors) names and marks exactly those, each with its reason:
    // an empty field that inherits a valid value is not missing.
    const listed = listedFieldErrors(error);
    const found = listed.map((item) => ({ item, inError: fieldInError(form, item.field) }));
    if (found.some(({ inError }) => inError !== null)) {
      markMissing(found.flatMap(({ inError }) => (inError ? [inError] : [])));
      setMissing([]);
      setInvalid([]);
      setNamed(
        found
          .map(({ item, inError }) => {
            const label = inError && inError.label !== "" ? inError.label : item.field;
            const reasonKey = `fieldReasons.${item.code}`;
            return i18n.exists(reasonKey, { ns: "errors" }) ? `${label} (${t(reasonKey as "generic")})` : label;
          })
          .join(t("listSeparator")),
      );
      return;
    }
    // The server named the fields it refused: mark those, not the required fields that only look empty.
    const refused = namedFields(form, error.args);
    if (refused.length > 0) {
      markMissing(refused);
      setMissing([]);
      setNamed(null);
      setInvalid([...new Set(refused.map((field) => field.label).filter(Boolean))]);
      return;
    }
    setInvalid([]);
    const fields = missingRequiredFields(form);
    markMissing(fields);
    setMissing([...new Set(fields.map((field) => field.label).filter(Boolean))]);
    // "A number is outside the allowed range" names no field: the API sends it (args.field), so mark and name it.
    const { field, role } = error.args;
    const inError =
      typeof field === "string" ? fieldInError(form, field, typeof role === "string" ? role : undefined) : null;
    if (inError) markMissing([inError]);
    setNamed(inError && inError.label !== "" ? inError.label : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the error decides what is marked; texts follow the language
  }, [error]);
  return (
    <MessageStrip ref={strip} design="Negative" hideCloseButton data-testid="api-error" className="positive-api-error">
      <span>
        {invalid.length > 0
          ? t("invalidFields", { message, fields: invalid.join(t("listSeparator")) })
          : missing.length === 0
            ? named === null
              ? message
              : t("checkField", { message, field: named })
            : isApiError(error) && error.code === "validation_failed"
              ? t("missingFields", { message, fields: missing.join(t("listSeparator")) })
              : t("requiredFields", { fields: missing.join(t("listSeparator")) })}
      </span>
      {traceId && (
        <span className="positive-api-error-trace" dir="ltr">
          {t("reference", { traceId })}
        </span>
      )}
      {onRetry && (
        <Button design="Transparent" onClick={onRetry} className="positive-api-error-retry">
          {t("retry")}
        </Button>
      )}
    </MessageStrip>
  );
}
